import { Box, Button, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import {
  batchStateLabel,
  exclusionCopy,
  isBatchRunning,
  isBatchStopped,
  patientCount,
} from '../bulkVocabulary';
import type { SubjectLanguage } from '../types';

import type { ExclusionBreakdownEntry } from './BulkLaunchPreview';

export interface BatchMessageCounts {
  /** Messages this batch will send in total. */
  total: number;
  queued: number;
  sent: number;
  delivered: number;
  failed: number;
  cancelled: number;
}

export interface BatchProgressPanelProps {
  requirementLabel: string;
  /** Raw server state; unknown values render as themselves rather than blanking. */
  state: string;
  counts: BatchMessageCounts;
  /**
   * Patients from this batch who have since satisfied the requirement — a
   * PATIENT outcome, not a message fact. Always reported by the merged
   * contract, so the old absent-versus-zero handling is gone with the state
   * that made it necessary.
   */
  completedPatients: number;
  /**
   * Patients the batch targeted but never messaged, carrying the preview's
   * exclusion reasons. **Not failures**, and rendered nowhere near them:
   * skipped is targeted-and-not-attempted, failed is attempted-and-refused,
   * and merging them would read a working exclusion rule as an incident.
   */
  skippedPatients?: number;
  /**
   * Why those patients were skipped AT LAUNCH, in the preview's vocabulary and
   * biggest first. Absent when the counts do not account for all of them, and
   * then the total stands alone rather than a partial answer standing for the
   * whole.
   */
  skippedBreakdown?: ExclusionBreakdownEntry[];
  subject: SubjectLanguage;
  isCancelling?: boolean;
  /**
   * Absent when this viewer may not cancel. Withheld, not disabled — a greyed
   * Stop button on someone else's batch invites a support ticket.
   */
  onCancel?: () => void;
  /**
   * Starts a new send once this one has stopped (GDN-4149). Offered only for a
   * finished or cancelled batch — the server allows one live batch per
   * requirement, and so does this panel. Absent when the caller
   * cannot tell whether a newer batch is live; withheld, not disabled.
   */
  onSendAgain?: () => void;
}

/**
 * The batch view on the requirement's catalog row (AC-12, AC-14): what happened
 * to the messages, what happened to the patients, and a way to stop it.
 */
export const BatchProgressPanel = ({
  requirementLabel,
  state,
  counts,
  completedPatients,
  skippedPatients,
  skippedBreakdown,
  subject,
  isCancelling = false,
  onCancel,
  onSendAgain,
}: BatchProgressPanelProps) => {
  const theme = useTheme();
  const running = isBatchRunning(state);
  const cancelled = state === 'cancelled';

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 0.5, alignItems: 'center' }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {requirementLabel}
        </Typography>
        <Chip
          size="small"
          label={batchStateLabel(state)}
          color={cancelled ? 'default' : running ? 'info' : 'success'}
          variant="outlined"
        />
      </Stack>

      {cancelled && (
        <Typography variant="body2" sx={{ mb: 1.5, color: theme.palette.text.secondary }}>
          Cancelled. Messages that had not gone out will not be sent. Anything already sent still
          counts, and nothing that was already answered was undone.
        </Typography>
      )}

      <Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap', rowGap: 1.5 }}>
        <Stat value={counts.queued} label="queued" tone={theme.palette.text.primary} />
        <Stat value={counts.sent} label="sent" tone={theme.palette.text.primary} />
        <Stat value={counts.delivered} label="delivered" tone={theme.palette.success.main} />
        <Stat value={counts.failed} label="failed" tone={theme.palette.error.main} />
        {/* AC-14 asks a cancelled batch to show final counts, and the count
            that makes it final is the one that never went out. Shown whenever
            the server reports any, not only in the cancelled state, so a
            partially-stopped batch still adds up. */}
        {counts.cancelled > 0 && (
          <Stat value={counts.cancelled} label="never sent" tone={theme.palette.text.secondary} />
        )}
      </Stack>
      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
        These count messages, not {subject.plural}: someone reachable by text and email gets two.
      </Typography>

      <Divider sx={{ my: 1.5 }} />
      <Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap', rowGap: 1.5 }}>
        <Stat
          value={completedPatients}
          label={`${subject.plural} have finished since this went out`}
          tone={theme.palette.primary.main}
        />
        {/* Its own number, deliberately beside the patient stats rather than
            among the message ones: these are people the batch chose not to
            message, for the reasons the preview already listed. */}
        {skippedPatients !== undefined && skippedPatients > 0 && (
          <Stat
            value={skippedPatients}
            label={`${subject.plural} skipped, not failed`}
            tone={theme.palette.text.secondary}
          />
        )}
      </Stack>

      {/*
        The reasons, in the preview's own words so one patient reads the same
        way in both places.

        Headed "skipped at launch" deliberately. These are the exclusions the
        batch made when it started; a cancelled batch's still-pending targets
        are NOT here — they sit in the outstanding count with no reason,
        because nothing was decided about them. A heading like "why nobody
        heard from us" would quietly account for the wrong population, and the
        numbers would look consistent while doing it.
      */}
      {skippedBreakdown?.length ? (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            Why these were skipped at launch
          </Typography>
          {skippedBreakdown.map(entry => {
            const copy = exclusionCopy(entry.reason);
            return (
              <Typography key={entry.reason} variant="body2" sx={{ mt: 0.5 }}>
                {entry.count.toLocaleString()} — {copy.label}
              </Typography>
            );
          })}
        </Box>
      ) : null}
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
        Delivered means the message arrived. This is{' '}
        {patientCount(completedPatients, subject.plural, subject.singular)} who actually did it.
      </Typography>

      {/*
        The count keeps moving after the batch stops, because the batch stops
        sending and the patients do not stop completing. Rather than hold a
        timer open on a finished batch — a standing poll on a number that has a
        better home — the panel says what it is showing. It re-fetches when
        staff reopen the row, so this stays true without one. PM ruling on
        GDN-3230 (AC-12), wording theirs.
      */}
      {!running && !isCancelling && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
          Completions continue after a batch ends. This count is from when the page loaded — the{' '}
          {subject.pluralCap} list shows the live number.
        </Typography>
      )}

      {(running || isCancelling) && onCancel && (
        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={onCancel}
            disabled={isCancelling}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {isCancelling ? 'Stopping…' : 'Stop unsent messages'}
          </Button>
        </Stack>
      )}

      {isBatchStopped(state) && !isCancelling && onSendAgain && (
        <Stack direction="row" spacing={1.5} sx={{ mt: 2, alignItems: 'center' }}>
          <Button variant="outlined" onClick={onSendAgain} sx={{ textTransform: 'none' }}>
            Send again…
          </Button>
          <Typography variant="caption" color="text.secondary">
            Shows who would get a request now before anything is sent.
          </Typography>
        </Stack>
      )}
    </Paper>
  );
};

const Stat = ({ value, label, tone }: { value: number; label: string; tone: string }) => (
  <Box sx={{ minWidth: 92 }}>
    <Typography
      sx={{
        fontSize: 24,
        fontWeight: 700,
        lineHeight: 1.1,
        color: tone,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value.toLocaleString()}
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
      {label}
    </Typography>
  </Box>
);
